import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import ErrorBoundary from '../../ErrorBoundry';
import { Col, Container, Row } from 'reactstrap';
import { Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { Icon } from '@progress/kendo-react-common';
import TelerikDataTable from '../../CommonComponents/TelerikDataTable/indexV2';
import visileColumns from './visibleColumns';
import { getUserRole, ShowError, ShowSucess } from '../../_helper/helper';
import { useAuth } from 'oidc-react';
import axios from 'axios';
import { loadingPanel } from '../../CommonComponents/CommonFunction';

const DocumentList = () => {
    const { userData } = useAuth();
    const id = "ApplicantsListByEligibility";

    const [page, setPage] = useState(1);
    const { columns, loadingSearch, ColumnsSorted, searchParameter, searchResultsV2 } = useSelector((state) => state.advanceSearchv2Reducer);
    const { filter: storeFilter, pageSize: storePageSize } = useSelector((state) => state.CommonReducer);

    const [additionalWhere, setAdditionalWhere] = useState("");
    const [sortData, setSortData] = useState([]);
    const [searchParams, setSearchParams] = useState([]);
    const [pageSize, setPageSize] = useState(storePageSize[id] || 100);
    const [sortedColumns, setSortedColumns] = useState(visileColumns);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    // For modal
    const [open, setOpen] = useState(false);
    const [formData, setFormData] = useState({
        sourceName: "",
        documentName: "",
        documentUrl: "",
        referenceId: "",
        remarks: "",
        employeeId: 0
    });

    const { results, rowCount } = useMemo(() => {
        const searchResult = searchResultsV2[id] || { results: [], rowCount: 0 };
        return {
            results: searchResult.results,
            rowCount: searchResult.rowCount,
        };
    }, [searchResultsV2, id]);

    const handlePageChange = (event) => {
        setPage(event.page.skip / event.page.take + 1);
        setPageSize(event.page.take);
    };

    const handleSortChange = (sort) => {
        setSortData(sort);
        setPage(1);
        setPageSize(100);
    };

    const handleFilterChange = (data) => {
        setAdditionalWhere(data);
        setPage(1);
        setPageSize(100);
    };

    const handlePageSizeChange = (event) => {
        setPageSize(event.target.value);
        setPage(1);
    };

    // ✅ Fetch documents by id
    const getDocumentById = () => {
        const newBaseURL = process.env.REACT_APP_API_DOTNET_URL;
        const docId = 36;
        setLoading(true);
        axios
            .get(`${newBaseURL}/BenefitLkDocumentSource/Getbyid${docId}`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("jwt_access_token")}`,
                    Role: getUserRole(userData),
                },
            })
            .then((res) => {
                const result = res?.data?.lkAttachments ?? [];
                setData(result);
                setLoading(false);
            })
            .catch((err) => console.warn(`Failed to fetch document with id ${docId}`, err));
    };

    useEffect(() => {
        getDocumentById();
    }, []);

    // ✅ POST: Add document
    const handleAddDocument = () => {
        if (formData.documentName.trim() === "") {
            ShowSucess("Please enter a document name");
            return;
        }

        const newBaseURL = process.env.REACT_APP_API_DOTNET_URL;

        // New payload structure
        const payload = [
            {
                contentType: formData.documentName,
                sourceId: 36, // Keeping the sourceId from the original component
                remarks: formData.remarks,
                isRequired: true // Assuming this is a required field
            }
        ];
        setLoading(true);

        axios
            // New API endpoint
            .post(`${newBaseURL}/BenefitLkDocumentSource/Attachmentcreate`, payload, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("jwt_access_token")}`,
                    Role: getUserRole(userData),
                },
            })
            .then(() => {
                setOpen(false);
                // Reset only the fields used in the form
                setFormData({ documentName: "", remarks: "" });
                getDocumentById(); // Refresh the data grid
                setLoading(false);
                ShowSucess("Document added successfully")
            })
            .catch((err) => ShowError("Error adding document:", err));
    };


    const handleDeleteDocument = (docId) => {
        if (!window.confirm("Are you sure you want to delete this document?")) return;
        const newBaseURL = process.env.REACT_APP_API_DOTNET_URL;
        setLoading(true);
        axios
            .delete(`${newBaseURL}/BenefitLkDocumentSource/AttachmentDelete?id=${docId}`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("jwt_access_token")}`,
                    Role: getUserRole(userData),
                },
            })
            .then(() => {
                getDocumentById()
                setLoading(false);
                ShowSucess("Document deleted successfully")
            })
            .catch((err) => ShowError(`Error deleting document ${docId}:`, err));
    };

    const actions = useMemo(() => [
        {
            title: "Delete",
            icon: { iconName: "k-icon k-i-trash" },
            className: "text-danger",
            style: { cursor: "pointer", color: "red" },
            action: (props) => handleDeleteDocument(props.dataItem.id),
        }
    ], []);

    return (
        <ErrorBoundary>
            <Fragment>
                <Container fluid={true}>
                    {loading && loadingPanel()}
                    <Row className="bg-primary rounded py-3 mb-3">
                        <Col md={6}>
                            <Typography variant="h5">
                                <Icon style={{ fontSize: "25px", margin: "10px" }} name="list-unordered-outline" />
                                Dependent Required Documents List
                            </Typography>
                        </Col>
                        <Col md={6}>
                            <div className="d-flex justify-content-end">
                                <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
                                    <Icon size="medium" name="plus" /> Add Document
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    <Row className="rounded py-3 mb-3">
                        <Col md={12}>
                            <TelerikDataTable
                                id={id}
                                pageNumber={page}
                                pageSize={pageSize}
                                totalCount={rowCount}
                                skip={(page - 1) * pageSize}
                                take={pageSize}
                                data={data || []}
                                sortData={sortData}
                                searchParams={searchParams}
                                additionalWhere={additionalWhere}
                                onFilterChange={handleFilterChange}
                                onSortChange={handleSortChange}
                                onPageChange={handlePageChange}
                                onPageSizeChange={handlePageSizeChange}
                                selectedColumns={sortedColumns}
                                setSortedColumns={setSortedColumns}
                                loading={loading}
                                enableFooterCell={false}
                                actions={actions}
                            />
                        </Col>
                    </Row>

                    {/* ➕ Add Document Modal */}
                    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                        <DialogTitle>Add Document</DialogTitle>
                        <DialogContent dividers>
                            <TextField
                                margin="dense"
                                label="Document Name"
                                fullWidth
                                value={formData.documentName}
                                onChange={(e) => setFormData({ ...formData, documentName: e.target.value })}
                            />
                            <TextField
                                margin="dense"
                                label="Remarks"
                                fullWidth
                                value={formData.remarks}
                                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOpen(false)}>Cancel</Button>
                            <Button variant="contained" onClick={handleAddDocument}>Save</Button>
                        </DialogActions>
                    </Dialog>
                </Container>
            </Fragment>
        </ErrorBoundary>
    );
};

export default DocumentList;
